import {
    VisitDatetimeField,
    VisitOutcome,
    VisitRequestState,
    VisitState,
} from '@axle-health/graphql-schema';

import { HOSPICE_DEMO_CONTAINER_KEYS } from '../containerKeys';
import { date, dateInDays } from '../graphInputHelpers';
import { QueueDefinition, RootEntity } from '../types';
import { DEFAULT_CONTAINER_FILTER_WINDOW_DAYS } from './pinnacleServiceIds';

/**
 * The hospice demo org. Its queues follow North Star's, keyed on service name
 * rather than service external ID, because a demo org's services carry no external
 * IDs to key on.
 *
 * Org service names the filters below reach for: Admission Visit; HOPE Update Visit
 * HUV1 and HUV2; SN Recertification Visit; Symptom Follow-Up Visit.
 *
 * Every queue is declared in full here, including the ones whose filters happen to
 * match another org's. A queue is per-org configuration, so changing what this org
 * counts means editing this file and no other.
 */

const ADMIT_VISIT_SERVICE_FILTER = {
    name: { iContains: 'Admission Visit' },
};

const IS_ACTIVE_BENEFIT_PERIOD_FILTER = {
    isActiveByDate: { exact: true },
    isActiveField: { exact: true },
};

const ADMITTED_BENEFIT_PERIOD_FILTER = {
    ...IS_ACTIVE_BENEFIT_PERIOD_FILTER,
    visits: {
        services: ADMIT_VISIT_SERVICE_FILTER,
        outcome: { exact: VisitOutcome.COMPLETED },
    },
};

const ADMITTED_BENEFIT_PERIOD_1_FILTER = {
    ...ADMITTED_BENEFIT_PERIOD_FILTER,
    numberInSequence: { exact: 1 },
};

// Eval visit requests still waiting to be scheduled.
const EVAL_VISIT_REQUESTS: QueueDefinition<RootEntity.VISIT_REQUEST> = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.evalVisitRequests,
    label: 'Evals',
    rootEntity: RootEntity.VISIT_REQUEST,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        services: { name: { iContains: 'eval' } },
        state: { exact: VisitRequestState.PENDING },
    }),
};

// Admission visits for active patients that carry no date yet.
const ADMISSIONS_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.admissionsVisits,
    label: 'Admissions visits',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        patient: { isActive: { exact: true } },
        services: ADMIT_VISIT_SERVICE_FILTER,
        state: { exact: VisitState.UNSCHEDULED },
    }),
};

// Patients due a HOPE Update Visit: HUV1 falls in days 6 through 15 of the benefit
// period and HUV2 in days 16 through 30, so each half looks back over the window
// the visit is owed in and excludes patients who already have that visit.
const HUV: QueueDefinition<RootEntity.PATIENT> = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.huv,
    label: 'HUVs',
    rootEntity: RootEntity.PATIENT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        episodes: {
            id: { isNull: false },
            isActiveByDate: { exact: true },
            startDate: {
                range: {
                    start: dateInDays(now, timezone, -14),
                    end: dateInDays(now, timezone, -5),
                },
            },
            NOT: {
                visits: {
                    services: { name: { iContains: 'HUV1' } },
                },
            },
        },
        OR: {
            episodes: {
                id: { isNull: false },
                isActiveByDate: { exact: true },
                startDate: {
                    range: {
                        start: dateInDays(now, timezone, -29),
                        end: dateInDays(now, timezone, -15),
                    },
                },
                NOT: {
                    visits: {
                        services: { name: { iContains: 'HUV2' } },
                    },
                },
            },
        },
    }),
};

// Admitted patients carrying a pending visit in a discipline their care team does
// not cover.
const INCOMPLETE_CARE_TEAM: QueueDefinition<RootEntity.PATIENT> = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.incompleteCareTeam,
    label: 'Incomplete care teams',
    rootEntity: RootEntity.PATIENT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        isActive: { exact: true },
        episodes: {
            ...IS_ACTIVE_BENEFIT_PERIOD_FILTER,
            visits: {
                services: ADMIT_VISIT_SERVICE_FILTER,
                outcome: { exact: VisitOutcome.COMPLETED },
            },
        },
        AND: {
            episodes: {
                ...IS_ACTIVE_BENEFIT_PERIOD_FILTER,
                visits: {
                    disciplineCoveredByCareTeam: { exact: false },
                    outcome: { exact: VisitOutcome.NONE },
                },
            },
        },
    }),
};

// Benefit periods with pending visit requests, reaching two weeks back so a period
// that has just ended still surfaces its unscheduled work.
const PATIENTS_NEEDING_SCHEDULING: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.patientsNeedingScheduling,
    label: 'Patients needing scheduling',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: false,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        endDate: { gte: dateInDays(now, timezone, -14) },
        visitRequests: { state: { exact: VisitRequestState.PENDING } },
    }),
};

// Follow-up visits owed within 48 hours of a Symptom Impact Assessment, where no
// follow-up in that window exists for the same patient and benefit period.
const SFV: QueueDefinition<RootEntity.VISIT> = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.sfv,
    label: 'SFVs',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        metadata: { hasKey: 'sfv_required' },
        relatedVisitCondition: {
            exists: false,
            filter: {
                services: { name: { iContains: 'Symptom Follow-Up Visit' } },
                outcome: { inList: [VisitOutcome.COMPLETED, VisitOutcome.NONE] },
            },
            timeConstraints: [
                {
                    field: VisitDatetimeField.EFFECTIVE_DATE,
                    relativeTo: VisitDatetimeField.TENTATIVE_DATE,
                    gteOffsetMinutes: 0,
                    lteOffsetMinutes: 2880, // 48 hours
                },
            ],
            samePatient: true,
            sameEpisode: true,
        },
    }),
};

// Pending visit requests whose fulfillment window closes within three days, or
// closed within the last month.
const EXPIRING_FULFILLMENT_WINDOW: QueueDefinition<RootEntity.VISIT_REQUEST> = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.expiringFulfillmentWindow,
    label: 'Expiring Fulfillment Windows',
    rootEntity: RootEntity.VISIT_REQUEST,
    snoozable: false,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        state: { exact: VisitRequestState.PENDING },
        fulfillmentWindowEndDate: {
            gte: dateInDays(now, timezone, -31),
            lte: dateInDays(now, timezone, 3),
        },
    }),
};

// Visits held in review because the patient did not show.
const PATIENT_NO_SHOWS: QueueDefinition<RootEntity.VISIT> = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.patientNoShows,
    label: 'Patient No-Shows',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        patient: { isActive: { exact: true } },
        state: { exact: VisitState.IN_REVIEW },
        outcome: { exact: VisitOutcome.PATIENT_DID_NOT_SHOW },
    }),
};

// Benefit periods ending within the window that hold no recert visit, either
// admitted first periods or any later period.
const RECERT: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.recert,
    label: 'Recerts',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: false,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        endDate: {
            range: {
                start: date(now, timezone),
                end: dateInDays(now, timezone, DEFAULT_CONTAINER_FILTER_WINDOW_DAYS),
            },
        },
        AND: {
            ...ADMITTED_BENEFIT_PERIOD_1_FILTER,
            OR: {
                ...IS_ACTIVE_BENEFIT_PERIOD_FILTER,
                numberInSequence: { gt: 1 },
            },
        },
        visits: {
            NOT: {
                services: { name: { iContains: 'Recertification Visit' } },
                outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
            },
        },
    }),
};

// Visits still waiting on a clinician.
const UNASSIGNED: QueueDefinition<RootEntity.VISIT> = {
    key: HOSPICE_DEMO_CONTAINER_KEYS.unassigned,
    label: 'Unassigned',
    rootEntity: RootEntity.VISIT,
    snoozable: false,
    heavyQuery: false,
    buildFilters: () => ({
        needsClinicians: { exact: true },
        outcome: { exact: VisitOutcome.NONE },
    }),
};

export const HOSPICE_DEMO_QUEUES: QueueDefinition[] = [
    EVAL_VISIT_REQUESTS,
    ADMISSIONS_VISITS,
    HUV,
    INCOMPLETE_CARE_TEAM,
    PATIENTS_NEEDING_SCHEDULING,
    UNASSIGNED,
    SFV,
    EXPIRING_FULFILLMENT_WINDOW,
    PATIENT_NO_SHOWS,
    RECERT,
];
